import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/invites/${token}`);
        setState({ loading: false, invite: data, error: null });
        setForm((f) => ({
          ...f,
          first_name: data.first_name || "",
          last_name: data.last_name || "",
        }));
      } catch (e) {
        setState({ loading: false, invite: null, error: e.response?.data?.detail || "Invite not found" });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.password || form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND}/api/public/invites/${token}/accept`,
        {
          password: form.password,
          first_name: form.first_name,
          last_name: form.last_name,
        },
        { withCredentials: true },
      );
      // Backend sets the httpOnly session cookie — JS storage not needed.
      toast.success("Welcome to the team");
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invite");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="accept-invite-page">
      <Card className="rounded-sm border-zinc-200 w-full max-w-md">
        <CardContent className="p-8 space-y-5">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 bg-[#002FA7] text-white rounded-sm flex items-center justify-center">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <div className="label-overline">Iceberg Digital</div>
              <h1 className="font-display font-extrabold text-2xl tracking-tight">Accept your invite</h1>
            </div>
          </div>

          {state.loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking invite…
            </div>
          )}

          {!state.loading && state.error && (
            <div className="space-y-3" data-testid="accept-invite-error">
              <p className="text-sm text-red-600">{state.error}</p>
              <Link to="/login" className="text-xs label-overline text-[#002FA7] hover:underline">
                Go to sign-in ↗
              </Link>
            </div>
          )}

          {!state.loading && state.invite && (
            <>
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs space-y-0.5">
                <div className="text-zinc-500">You&apos;ve been invited by</div>
                <div className="font-display font-bold text-sm">{state.invite.invited_by_name || "an administrator"}</div>
                <div className="text-zinc-500 pt-2">Email</div>
                <div className="font-mono">{state.invite.email}</div>
                <div className="text-zinc-500 pt-2">Role</div>
                <div className="font-display font-bold capitalize">{state.invite.role}</div>
              </div>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="First name">
                    <Input value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      className="rounded-sm h-10" data-testid="accept-invite-first-name" />
                  </Field>
                  <Field label="Last name">
                    <Input value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      className="rounded-sm h-10" data-testid="accept-invite-last-name" />
                  </Field>
                </div>
                <Field label="Choose a password (min 8 chars)" required>
                  <Input type="password" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="rounded-sm h-10" data-testid="accept-invite-password" required />
                </Field>
                <Field label="Confirm password" required>
                  <Input type="password" value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    className="rounded-sm h-10" data-testid="accept-invite-confirm" required />
                </Field>
                <Button type="submit" disabled={busy}
                  className="w-full rounded-sm bg-zinc-900 hover:bg-black text-white h-10"
                  data-testid="accept-invite-submit">
                  {busy ? "Setting up…" : "Set password & sign in"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-overline">
        {label} {required && <span className="text-red-600 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
